import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Company } from '../api/entities.js';
import { Ticket } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { 
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Mail,
  Users,
  Link as LinkIcon,
  Edit,
  Clock,
  FileText,
  CreditCard
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ViewCompany() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Obter ID da empresa da URL
      const urlParams = new URLSearchParams(window.location.search);
      const companyId = urlParams.get('id');
      
      if (!companyId) {
        navigate(createPageUrl('Companies'));
        return;
      }

      // Carregar usuário atual
      const userData = await User.me();
      setCurrentUser(userData);

      // Verificar permissões
      if (userData.role === 'client' && userData.company_id !== companyId) {
        navigate(createPageUrl('Dashboard'));
        return;
      }

      // Carregar dados da empresa
      const companyData = await Company.findById(companyId);
      if (!companyData) {
        navigate(createPageUrl('Companies'));
        return;
      }
      setCompany(companyData);

      // Carregar chamados da empresa
      const ticketsData = await Ticket.list({ company_id: companyId });
      setTickets(Array.isArray(ticketsData) ? ticketsData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []);

    } catch (error) {
      console.error('Error loading company:', error);
      navigate(createPageUrl('Companies'));
    }
    setIsLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">Empresa não encontrada</h2>
        <p className="text-muted-foreground mt-2">
          A empresa solicitada não existe ou foi removida.
        </p>
        <Button 
          onClick={() => navigate(createPageUrl('Companies'))}
          className="mt-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Empresas
        </Button>
      </div>
    );
  }

  const isAdmin = currentUser?.role !== 'client';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Companies'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{company.corporate_name}</h1>
            {company.trade_name && (
              <p className="text-muted-foreground mt-1">{company.trade_name}</p>
            )}
          </div>
        </div>

        {isAdmin && (
          <Button 
            onClick={() => navigate(createPageUrl(`EditCompany?id=${company.id}`))}
            className="bg-primary"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar Empresa
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Informações da Empresa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">CNPJ</p>
                  <p className="mt-1">{company.cnpj}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge 
                    className={
                      company.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }
                  >
                    {company.status === 'active' ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>

              {/* Endereço */}
              {company.address && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Endereço</h3>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-1" />
                    <p>
                      {company.address.street}, {company.address.number}
                      {company.address.complement && `, ${company.address.complement}`}
                      <br />
                      {company.address.district} - {company.address.city}/{company.address.state}
                      <br />
                      CEP: {company.address.zip_code}
                    </p>
                  </div>
                </div>
              )}

              {/* Contatos */}
              {company.contacts && company.contacts.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Contatos</h3>
                  <div className="space-y-3">
                    {company.contacts.map((contact, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="font-medium">{contact.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {contact.role && `${contact.role} • `}
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && ` • ${contact.phone}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Acessos Remotos */}
              {isAdmin && company.remote_access && company.remote_access.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Acessos Remotos</h3>
                  <div className="space-y-3">
                    {company.remote_access.map((access, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <LinkIcon className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="font-medium">{access.type.toUpperCase()}</p>
                          <p className="text-sm text-muted-foreground">
                            ID: {access.identifier}
                            {access.notes && ` • ${access.notes}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Abas de Conteúdo */}
          <Tabs defaultValue="tickets">
            <TabsList>
              <TabsTrigger value="tickets">Chamados</TabsTrigger>
              <TabsTrigger value="systems">Sistemas</TabsTrigger>
            </TabsList>

            <TabsContent value="tickets" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Últimos Chamados</CardTitle>
                    <Button 
                      variant="outline"
                      onClick={() => navigate(createPageUrl('Tickets'))}
                    >
                      Ver todos
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {tickets.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground">Nenhum chamado encontrado</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {tickets.slice(0, 5).map((ticket) => (
                        <div 
                          key={ticket.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                          onClick={() => navigate(createPageUrl(`ViewTicket?id=${ticket.id}`))}
                        >
                          <div>
                            <h4 className="font-medium">{ticket.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              #{ticket.id.slice(0, 8)} • {format(new Date(ticket.created_at), 'dd/MM/yyyy')}
                            </p>
                          </div>
                          <Badge
                            className={
                              ticket.status === 'open' ? 'bg-red-100 text-red-800' :
                              ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {
                              ticket.status === 'open' ? 'Aberto' :
                              ticket.status === 'in_progress' ? 'Em Andamento' :
                              ticket.status === 'resolved' ? 'Resolvido' :
                              ticket.status
                            }
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>



            <TabsContent value="systems" className="mt-6">
              {/* TODO: Implementar lista de sistemas */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Sistemas</CardTitle>
                    {isAdmin && (
                      <Button
                        onClick={() => navigate(createPageUrl(`NewSystem?company_id=${company.id}`))}
                      >
                        Adicionar Sistema
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="text-muted-foreground">
                      Nenhum sistema configurado
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Barra Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estatísticas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{tickets.length}</p>
                  <p className="text-sm text-muted-foreground">Chamados</p>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <FileText className="h-5 w-5 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-sm text-muted-foreground">Sistemas</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  Status dos Chamados
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Em Aberto</span>
                    <span className="font-medium">
                      {tickets.filter(t => t.status === 'open').length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Em Andamento</span>
                    <span className="font-medium">
                      {tickets.filter(t => t.status === 'in_progress').length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Resolvidos</span>
                    <span className="font-medium">
                      {tickets.filter(t => t.status === 'resolved').length}
                    </span>
                  </div>
                </div>
              </div>


            </CardContent>
          </Card>

          {/* Contratos */}
          {company.contracts && company.contracts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contratos Ativos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.contracts.map((contract, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <Badge>
                        {contract.type === 'support' ? 'Suporte' :
                         contract.type === 'development' ? 'Desenvolvimento' :
                         contract.type === 'project' ? 'Projeto' :
                         contract.type}
                      </Badge>
                      <Badge
                        className={
                          contract.status === 'active' ? 'bg-green-100 text-green-800' :
                          contract.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }
                      >
                        {contract.status === 'active' ? 'Ativo' :
                         contract.status === 'pending' ? 'Pendente' :
                         contract.status === 'finished' ? 'Finalizado' :
                         contract.status}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm">{contract.description}</p>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <p>Início: {format(new Date(contract.start_date), 'dd/MM/yyyy')}</p>
                      {contract.end_date && (
                        <p>Término: {format(new Date(contract.end_date), 'dd/MM/yyyy')}</p>
                      )}
                    </div>
                    <p className="mt-2 font-medium">
                      {formatCurrency(contract.value)}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* SLA Config */}
          {company.sla_config && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Configuração de SLA</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">
                      Suporte
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Baixa</span>
                        <span className="font-medium">{company.sla_config.support.low}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Média</span>
                        <span className="font-medium">{company.sla_config.support.medium}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Alta</span>
                        <span className="font-medium">{company.sla_config.support.high}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Crítica</span>
                        <span className="font-medium">{company.sla_config.support.critical}h</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">
                      Desenvolvimento
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Baixa</span>
                        <span className="font-medium">{company.sla_config.development.low}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Média</span>
                        <span className="font-medium">{company.sla_config.development.medium}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Alta</span>
                        <span className="font-medium">{company.sla_config.development.high}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Crítica</span>
                        <span className="font-medium">{company.sla_config.development.critical}h</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}